import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number} */
  #pendingAdds = 0;

  /** Keeps the compact insurance option in sync with the third-party widget. */
  #insuranceObserver = new MutationObserver(() => this.#syncInsuranceOption());

  /** Prevent duplicate shopper clicks while allowing the app’s native checkout handoff. */
  #insuranceCheckoutPending = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleCheckoutClick);
    this.#insuranceObserver.observe(this, {
      attributes: true,
      attributeFilter: ['style', 'disabled'],
      childList: true,
      subtree: true,
    });
    this.#syncInsuranceOption();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleCheckoutClick);
    this.#insuranceObserver.disconnect();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#syncInsuranceOption();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Replaces the insurance app's duplicate checkout stack with one compact,
   * optional control. The app still owns eligibility, pricing, and checkout.
   */
  #syncInsuranceOption = () => {
    const option = this.querySelector('[data-cart-insurance-option]');
    if (!(option instanceof HTMLElement)) return;

    const appButton = this.querySelector('.insure-shield-div .checkout-cart-widget');
    const isAvailable =
      appButton instanceof HTMLButtonElement && appButton.style.display !== 'none' && !appButton.disabled;

    option.toggleAttribute('hidden', !isAvailable);

    const appPrice = this.querySelector('.insure-shield-div .insurance-price-text')?.textContent || '';
    const priceMatch = appPrice.match(/\+\s*([^\s]+)/);
    const priceOutput = option.querySelector('[data-cart-insurance-price]');
    const compactPrice = priceMatch ? `+${priceMatch[1]}` : '';

    if (priceOutput && priceOutput.textContent !== compactPrice) {
      priceOutput.textContent = compactPrice;
    }

    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Uses the same primary checkout button for both paths. When protection is
   * selected, the insurance app adds coverage and submits the native checkout.
   *
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutButton = event.target.closest('.cart__checkout-button');
    if (!(checkoutButton instanceof HTMLButtonElement) || !this.contains(checkoutButton)) return;

    if (this.#insuranceCheckoutPending) {
      // InsureShield finishes by programmatically clicking this same button.
      if (!event.isTrusted) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const insuranceCheckbox = this.querySelector('[data-cart-insurance-checkbox]');
    if (!(insuranceCheckbox instanceof HTMLInputElement) || !insuranceCheckbox.checked) return;

    const appButton = this.querySelector('.insure-shield-div .checkout-cart-widget');
    if (
      !(appButton instanceof HTMLButtonElement) ||
      appButton.style.display === 'none' ||
      appButton.disabled
    ) {
      insuranceCheckbox.checked = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const buttonText = checkoutButton.querySelector('.button-text');
    const originalText = buttonText?.textContent || 'Checkout';
    this.#insuranceCheckoutPending = true;
    checkoutButton.setAttribute('aria-busy', 'true');
    if (buttonText) buttonText.textContent = 'Adding protection…';

    try {
      // Await the app's workflow so its synthetic checkout click can submit the form.
      const insuranceApp = /** @type {Window & { checkoutCart?: (event: MouseEvent) => Promise<void> }} */ (window);
      if (typeof insuranceApp.checkoutCart !== 'function') return;
      await insuranceApp.checkoutCart(event);
    } finally {
      this.#insuranceCheckoutPending = false;
      checkoutButton.removeAttribute('aria-busy');
      if (buttonText) buttonText.textContent = originalText;
    }
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (event.action === 'add') {
      this.#setAddPending(true);
    }

    // Open as soon as the request starts. A lightweight loading state prevents
    // an empty-cart flash while the updated drawer section is being rendered.
    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ cart, detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (Number.isFinite(Number(cart?.totalQuantity))) {
          this.#updateDrawerBadge(Number(cart.totalQuantity));
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      })
      .finally(() => {
        if (event.action !== 'add') return;

        // Let the cart-items promise handler finish its DOM morph before the
        // loading veil fades out.
        requestAnimationFrame(() => requestAnimationFrame(() => this.#setAddPending(false)));
      });
  };

  /** @param {boolean} pending */
  #setAddPending(pending) {
    this.#pendingAdds = Math.max(0, this.#pendingAdds + (pending ? 1 : -1));
    const isPending = this.#pendingAdds > 0;

    this.classList.toggle('cart-drawer--updating', isPending);
    for (const content of this.querySelectorAll('.cart-drawer__content')) {
      if (content instanceof HTMLElement) content.inert = isPending;
    }
    this.#dialog?.setAttribute('aria-busy', String(isPending));
  }

  /** @param {number} itemCount */
  #updateDrawerBadge(itemCount) {
    const badge = this.querySelector('.theme-drawer__badge');
    if (!badge) return;

    badge.textContent = itemCount > 99 ? '99+' : String(itemCount);
    badge.classList.toggle('visually-hidden', itemCount === 0);
    badge.toggleAttribute('data-maintain-ratio', itemCount <= 99);
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
